import React, { useState, useEffect, useRef } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useContent } from '../contexts/ContentContext';
import {
    Cpu, Globe, Phone, Mail, CheckCircle2, ShieldAlert, ChevronRight
} from 'lucide-react';

type ContextType = {
    onOpenBooking: (type?: string) => void;
    onOpenContact: (subject?: string) => void;
};

const STATICFORMS_ACCESS_KEY = 'sf_blc2ghcmjnneb6722gdjhmng';
const RECAPTCHA_V2_SITE_KEY = '6LdcK1csAAAAAKQ5U6x1jbLtwqNze3Fdm6q1dlfa';

interface ContactSectionProps {
    id?: string;
    className?: string;
}

export const ContactSection: React.FC<ContactSectionProps> = ({ id = 'contact', className = '' }) => {
    const { content, t, lang, consent, updateConsent } = useContent();
    const { onOpenContact } = useOutletContext<ContextType>();

    const contactInfo = content.contact || { hq: {}, hubs: [] };

    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [captchaToken, setCaptchaToken] = useState<string>('');
    const recaptchaWidgetId = useRef<any>(null);

    const [formData, setFormData] = useState({
        name: '', email: '', company: '', projectType: 'full-cycle',
        message: '', honeypot: '', agreeToPrivacy: false
    });

    const [touched, setTouched] = useState<Record<string, boolean>>({});

    const fieldErrors = {
        name: formData.name.trim().length < 2 ? (lang === 'en' ? 'Full name is required' : 'Ime i prezime su obavezni') : null,
        email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? (lang === 'en' ? 'Invalid email format' : 'Neispravan format emaila') : null,
        message: formData.message.trim().length < 10 ? (lang === 'en' ? 'Message must be at least 10 characters' : 'Poruka mora imati najmanje 10 karaktera') : null,
        privacy: !formData.agreeToPrivacy ? (lang === 'en' ? 'You must agree to privacy policy' : 'Morate prihvatiti polisu privatnosti') : null,
        captcha: !captchaToken ? (lang === 'en' ? 'Please complete reCAPTCHA' : 'Molimo potvrdite reCAPTCHA') : null
    };

    const isFormValid = !fieldErrors.name && !fieldErrors.email && !fieldErrors.message && !fieldErrors.privacy && captchaToken !== '';

    // Handle Privacy Checkbox change
    const handlePrivacyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        setFormData(prev => ({ ...prev, agreeToPrivacy: isChecked }));

        // If user manually checks the box, we grant marketing consent (needed for reCAPTCHA)
        if (isChecked) {
            updateConsent({ marketing: true });
        }
    };

    // reCAPTCHA Rendering Logic
    useEffect(() => {
        const shouldRender = formData.agreeToPrivacy || consent.marketing;

        if (status !== 'success' && shouldRender) {
            const renderRecaptcha = () => {
                const container = document.getElementById('recaptcha-contact-section');
                if (container && container.innerHTML === "") {
                    if (window.grecaptcha && window.grecaptcha.render) {
                        try {
                            recaptchaWidgetId.current = window.grecaptcha.render('recaptcha-contact-section', {
                                sitekey: RECAPTCHA_V2_SITE_KEY,
                                theme: 'dark',
                                callback: (token: string) => setCaptchaToken(token),
                                'expired-callback': () => setCaptchaToken(''),
                            });
                        } catch (e) {
                            console.warn("reCAPTCHA render error:", e);
                        }
                    } else {
                        setTimeout(renderRecaptcha, 500);
                    }
                }
            };
            setTimeout(renderRecaptcha, 200);
        }
    }, [status, formData.agreeToPrivacy, consent.marketing]);

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched({ name: true, email: true, message: true, privacy: true, captcha: true });
        if (formData.honeypot) return;
        if (!isFormValid) return;
        setStatus('submitting');
        setErrorMessage(null);
        try {
            const response = await fetch("https://api.staticforms.xyz/submit", {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({
                    accessKey: STATICFORMS_ACCESS_KEY,
                    name: formData.name,
                    email: formData.email,
                    subject: `Saltium Contact: ${formData.name}`,
                    message: `Project: ${formData.projectType}\nCompany: ${formData.company}\n\n${formData.message}`,
                    replyTo: formData.email,
                    "g-recaptcha-response": captchaToken
                }),
            });
            const result = await response.json();

            const isActuallySuccess = result.success || (result.message && result.message.toLowerCase().includes('submitted successfully'));

            if (isActuallySuccess) {
                setStatus('success');
            } else {
                setStatus('error');
                setErrorMessage(result.message || "Archive transmission failed.");
                if (window.grecaptcha && recaptchaWidgetId.current !== null) {
                    window.grecaptcha.reset(recaptchaWidgetId.current);
                }
                setCaptchaToken('');
            }
        } catch (error) {
            console.error("Form error:", error);
            setStatus('error');
            setErrorMessage("Technical disconnect. Please check your network.");
        }
    };

    return (
        <section id={id} className={`py-32 bg-[#120e16] scroll-mt-[80px] border-t border-white/5 relative overflow-hidden ${className}`}>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent"></div>
            <div className="absolute inset-0 bg-grid opacity-20"></div>
            <div className="max-w-6xl mx-auto px-4 relative z-10">
                <div className="grid lg:grid-cols-5 gap-20 items-start">
                    <div className="lg:col-span-2 space-y-12">
                        <div>
                            <span className="text-accent font-bold uppercase tracking-[0.5em] text-xs mb-6 block">{t('contact_label')}</span>
                            <h2 className="text-5xl font-gaming font-bold text-white uppercase tracking-tighter leading-none mb-8">
                                {lang === 'en' ? <>Let's Build <span className="text-accent">Together</span></> : <>Gradimo <span className="text-accent">Zajedno</span></>}
                            </h2>
                            <p className="text-gray-400 text-lg font-light leading-golden antialiased">{t('contact_subtitle')}</p>
                        </div>
                        <div className="space-y-10">
                            <div className="flex items-start gap-6 group">
                                <div className="w-14 h-14 rounded-[20px] bg-[#1a141f] border border-white/10 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-white transition-all shrink-0"><Cpu size={24} /></div>
                                <div>
                                    <p className="text-xs uppercase font-bold text-gray-400 tracking-widest mb-1">Global HQ</p>
                                    <p className="text-white font-bold tracking-wider">{contactInfo.hq.name}</p>
                                    <p className="text-gray-400 text-xs mt-1 leading-golden">{contactInfo.hq.address}<br />{contactInfo.hq.city}, {contactInfo.hq.country}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-6 group">
                                <div className="w-14 h-14 rounded-[20px] bg-[#1a141f] border border-white/10 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-white transition-all shrink-0"><Globe size={24} /></div>
                                <div>
                                    <p className="text-xs uppercase font-bold text-gray-400 tracking-widest mb-1">Production Hubs</p>
                                    <p className="text-white font-bold tracking-wider">Saltium Games BiH</p>
                                    <p className="text-gray-400 text-xs mt-1 tracking-widest">{contactInfo.hubs.map((h: any) => h.name).join(' | ')}</p>
                                </div>
                            </div>
                            <div className="space-y-6 pt-6 border-t border-white/5">
                                <div className="flex items-center gap-4">
                                    <Phone className="text-accent w-5 h-5" />
                                    <div>
                                        <p className="text-xs uppercase font-bold text-gray-400 tracking-widest">Direct Line</p>
                                        <p className="text-white font-gaming font-bold tracking-wider">{contactInfo.phone}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <Mail className="text-accent w-5 h-5" />
                                    <div>
                                        <p className="text-xs uppercase font-bold text-gray-400 tracking-widest">Electronic Mail</p>
                                        <p className="text-white font-gaming font-bold tracking-wider hover:text-accent transition-colors cursor-pointer" onClick={() => onOpenContact()}>{contactInfo.email}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-3">
                        <div className="bg-[#1a141f] p-6 sm:p-10 md:p-14 rounded-[32px] md:rounded-[50px] border border-white/5 relative overflow-hidden shadow-3xl min-h-0 md:min-h-[600px] flex flex-col justify-center">
                            {status === 'success' ? (
                                <div className="text-center py-10 animate-in fade-in zoom-in duration-700">
                                    <div className="relative inline-block mb-10">
                                        <div className="absolute inset-0 bg-accent/20 blur-3xl animate-pulse rounded-full"></div>
                                        <div className="relative w-28 h-28 bg-accent/10 border-2 border-accent/40 rounded-[35px] flex items-center justify-center mx-auto shadow-[0_0_50px_rgba(249,115,22,0.3)]">
                                            <CheckCircle2 size={56} className="text-accent" strokeWidth={1.5} />
                                        </div>
                                    </div>
                                    <h3 className="text-4xl md:text-5xl font-gaming font-bold text-white mb-6 uppercase tracking-tighter leading-none">{t('form_success')}</h3>
                                    <div className="space-y-4 max-w-sm mx-auto">
                                        <p className="text-gray-400 text-lg font-light leading-relaxed italic">
                                            {lang === 'en'
                                                ? "Our leads will analyze your vision and respond shortly."
                                                : "Naši inženjeri će analizirati vašu viziju i odgovoriti ubrzo."}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => { setStatus('idle'); setTouched({}); setCaptchaToken(''); }}
                                        className="mt-12 text-accent font-bold uppercase tracking-widest text-[10px] hover:text-white transition-colors border-b border-accent/20 pb-1"
                                    >
                                        {lang === 'en' ? "Send another message" : "Pošalji novu poruku"}
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleFormSubmit} className="space-y-6 relative z-10" noValidate>
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 ml-1">{t('form_name')} *</label>
                                            <input type="text" name="name" placeholder="Your Name" className={`w-full bg-[#120e16] border ${touched.name && fieldErrors.name && status === 'idle' ? 'border-red-500' : 'border-white/5'} rounded-2xl px-5 py-3.5 md:px-6 md:py-4 outline-none focus:border-accent transition-all text-white placeholder-gray-500 text-sm`} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} onBlur={() => setTouched({ ...touched, name: true })} />
                                            {touched.name && fieldErrors.name && status === 'idle' && <p className="text-[10px] text-red-500 uppercase font-bold tracking-widest ml-1 animate-in slide-in-from-top-1">{fieldErrors.name}</p>}
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 ml-1">{t('form_email')} *</label>
                                            <input type="email" name="email" placeholder="email@company.com" className={`w-full bg-[#120e16] border ${touched.email && fieldErrors.email && status === 'idle' ? 'border-red-500' : 'border-white/5'} rounded-2xl px-5 py-3.5 md:px-6 md:py-4 outline-none focus:border-accent transition-all text-white placeholder-gray-500 text-sm`} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} onBlur={() => setTouched({ ...touched, email: true })} />
                                            {touched.email && fieldErrors.email && status === 'idle' && <p className="text-[10px] text-red-500 uppercase font-bold tracking-widest ml-1 animate-in slide-in-from-top-1">{fieldErrors.email}</p>}
                                        </div>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 ml-1">{t('form_company')}</label>
                                            <input type="text" name="company" placeholder="Organization Name" className="w-full bg-[#120e16] border border-white/5 rounded-2xl px-5 py-3.5 md:px-6 md:py-4 outline-none focus:border-accent transition-all text-white placeholder-gray-500 text-sm" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
                                        </div>
                                        <div className="space-y-1">
                                            <label htmlFor="project_type" className="text-[10px] font-bold uppercase tracking-widest text-gray-400 ml-1">{t('form_project')}</label>
                                            <div className="relative">
                                                <select id="project_type" name="project_type" className="w-full bg-[#120e16] border border-white/5 rounded-2xl px-5 py-3.5 md:px-6 md:py-4 outline-none focus:border-accent transition-all text-white appearance-none cursor-pointer text-sm" value={formData.projectType} onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}>
                                                    <option value="full-cycle">Full-Cycle Game Dev</option>
                                                    <option value="optimization">Performance & Optimization</option>
                                                    <option value="art">3D/2D Art Services</option>
                                                    <option value="xr">XR & Metaverse Solutions</option>
                                                    <option value="porting">Game Porting & Migration</option>
                                                    <option value="qa">Quality Assurance & Testing</option>
                                                    <option value="uiux">UI/UX Design for Games</option>
                                                    <option value="liveops">Backend & LiveOps Support</option>
                                                    <option value="tools">Custom Engine Tooling</option>
                                                    <option value="consulting">Technical Consulting</option>
                                                </select>
                                                <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                                    <ChevronRight size={16} className="rotate-90" aria-hidden="true" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 ml-1">{t('form_message')} *</label>
                                        <textarea name="message" placeholder="Tell us about your technical and creative goals..." className={`w-full bg-[#120e16] border ${touched.message && fieldErrors.message && status === 'idle' ? 'border-red-500' : 'border-white/10'} rounded-2xl px-5 py-3.5 md:px-6 md:py-4 outline-none focus:border-accent transition-all text-white h-32 resize-none placeholder-gray-500 text-sm leading-golden`} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} onBlur={() => setTouched({ ...touched, message: true })} />
                                        {touched.message && fieldErrors.message && status === 'idle' && <p className="text-[10px] text-red-500 uppercase font-bold tracking-widest ml-1 animate-in slide-in-from-top-1">{fieldErrors.message}</p>}
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <div className="flex items-center gap-3">
                                            <input type="checkbox" id="privacy-consent" checked={formData.agreeToPrivacy} onChange={handlePrivacyChange} className="w-4 h-4 accent-accent cursor-pointer shrink-0" />
                                            <label htmlFor="privacy-consent" className={`text-[10px] md:text-xs ${touched.privacy && fieldErrors.privacy && status === 'idle' ? 'text-red-500 font-black' : 'text-gray-400 font-bold'} uppercase tracking-widest cursor-pointer leading-tight`}>
                                                {t('form_consent')} <Link to="/privacy-policy" target="_blank" className="text-accent underline hover:text-white transition-colors">{t('privacy_title')}</Link>
                                            </label>
                                        </div>
                                        {touched.privacy && fieldErrors.privacy && status === 'idle' && <p className="text-[10px] text-red-500 uppercase font-bold tracking-widest ml-7 animate-in slide-in-from-top-1">{fieldErrors.privacy}</p>}
                                    </div>
                                    <div className="pt-4 space-y-6">
                                        <div id="recaptcha-contact-section" data-theme="dark" className="flex justify-center md:justify-start"></div>
                                        {touched.captcha && fieldErrors.captcha && status === 'idle' && <p className="text-[10px] text-red-500 uppercase font-bold tracking-widest ml-1">{fieldErrors.captcha}</p>}
                                        {errorMessage && status === 'error' && (
                                            <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-xl flex items-center gap-3 text-red-500 text-xs font-bold uppercase tracking-widest animate-in fade-in">
                                                <ShieldAlert size={16} />
                                                {errorMessage}
                                            </div>
                                        )}
                                        <button id="cta-contact-submit" type="submit" disabled={status === 'submitting'} className={`btn-primary w-full py-5 md:py-6 text-sm transition-all ${!isFormValid && status === 'idle' ? 'opacity-50 grayscale cursor-not-allowed' : ''}`}>
                                            {status === 'submitting' ? 'Transmitting...' : t('form_btn')}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
